import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { X, Plus, ChevronDown, Loader2 } from "lucide-react";
import { VEHICLE_IMAGE_TYPE_OPTIONS } from "../../../../api/dealership/dealershipModel";

export const VehicleImages = ({
  images = [],
  onAddFiles,
  onRemoveVehicleImage,
  onOpenLabelPicker,
  loading = false,
  error = false,
  buttonTitle = "Upload Vehicle Photos",
  errorMessage,
  typeOptions,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);

  const getLabelTitle = (val) => {
    if (!val || val === "unknown") return t("realEstate.selectType") || "Select Type";
    const locKey = `listingLabels.${val}`;
    const loc = t(locKey);
    if (loc && loc !== locKey) return loc;
    const optionsToSearch = typeOptions || VEHICLE_IMAGE_TYPE_OPTIONS;
    const found = optionsToSearch.find((item) => item.value === val);
    return found ? found.label : val;
  };

  const handleFileInputChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      onAddFiles(files);
    }
    e.target.value = "";
  };

  return (
    <div className="w-full space-y-3">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/jpeg,image/png,image/webp,image/jpg"
        onChange={handleFileInputChange}
        className="hidden"
      />

      {/* Horizontal thumbnail list */}
      {images.length > 0 && (
        <div className="flex items-center gap-3 overflow-x-auto pb-2 pt-2 px-1 no-scrollbar">
          {images.map((item, index) => {
            const isUnknown = !item.label || item.label === "unknown";
            const imageUrl =
              typeof item === "string"
                ? item
                : item.preview || item.uri || item.image || item.url;

            return (
              <div
                key={item.tempId || item.id || index}
                className="relative flex-shrink-0 w-24 sm:w-28 flex flex-col items-center gap-1.5"
              >
                {/* Image Box */}
                <div className="relative w-24 h-24 sm:w-28 sm:h-28 rounded-2xl overflow-hidden bg-gray-100 border border-gray-200 shadow-xs">
                  <img
                    src={imageUrl}
                    alt=""
                    className="w-full h-full object-cover"
                  />

                  {/* Loading spinner overlay if AI processing or uploading */}
                  {item.isLoading && (
                    <div className="absolute inset-0 bg-black/40 backdrop-blur-xs flex items-center justify-center text-white">
                      <Loader2 className="w-5 h-5 animate-spin" />
                    </div>
                  )}

                  {/* Floating Delete Button */}
                  <button
                    type="button"
                    onClick={() => onRemoveVehicleImage(index)}
                    className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-white/95 shadow-sm text-red-500 hover:text-red-700 hover:scale-110 flex items-center justify-center transition-all"
                  >
                    <X className="w-3.5 h-3.5 stroke-[2.5]" />
                  </button>
                </div>

                {/* Bottom Label Picker Button */}
                <button
                  type="button"
                  onClick={() => onOpenLabelPicker(index)}
                  className={`w-full flex items-center justify-between px-2 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    isUnknown
                      ? "border border-red-500 bg-red-50 text-red-600 hover:bg-red-100"
                      : "border border-gray-200 bg-white text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <span className="truncate max-w-[80px]">
                    {item.isLoading
                      ? t("analyzing", "Analyzing...")
                      : getLabelTitle(item.label)}
                  </span>
                  <ChevronDown className="w-3 h-3 flex-shrink-0 ml-1 text-gray-400" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Error message */}
      {error && (
        <p className="text-xs text-red-500 font-medium pt-1">
          {errorMessage || t("profile.selectExteriorImageTypes") || "* Please select all image types"}
        </p>
      )}

      {/* Upload button */}
      <button
        type="button"
        disabled={loading}
        onClick={() => fileInputRef.current?.click()}
        className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl border border-dashed border-gray-300 hover:border-[#020637] hover:bg-[#eef0f8]/30 text-gray-700 hover:text-[#020637] text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed bg-white"
      >
        {loading ? (
          <Loader2 className="w-4 h-4 animate-spin text-[#020637]" />
        ) : (
          <Plus className="w-4 h-4 stroke-[2.5]" />
        )}
        <span>{loading ? t("loading") || "Loading..." : buttonTitle}</span>
      </button>
    </div>
  );
};
